import { t } from '../../common/i18n.js'
import { SignatureInsertWithRows, SignatureItem, SignatureUpdate } from '../../common/types/api.js'
import { Language } from '../../common/types/i18n.js'
import { InfoRow } from '../modules/signature-creation/signature-info-rows.js'

type LogoTag = 'play' | 'nope' | 'alliance'

export interface SignatureFormData {
    name: string
    position: string
    email: string
    tags: LogoTag[]
    rows: InfoRow[]
    imageUrl: string | undefined
    company?: 'nope' | 'play'
}

export interface ValidationError {
    field: string
    message: string
}
export class ValidationErrorClass extends Error {
    public readonly field: string
    public readonly name = 'ValidationError'

    constructor(field: string, message: string) {
        super(message)
        this.field = field
        Object.setPrototypeOf(this, ValidationErrorClass.prototype)
    }
}

export const validateSignatureFormData = (
    formData: SignatureFormData,
    lang: Language,
): ValidationError | null => {
    const name = (formData.name || '').trim()
    const position = (formData.position || '').trim()
    const email = (formData.email || '').trim()

    if (name === '' && position === '' && email === '') {
        return {
            field: 'required',
            message: t.signature.validationRequired(lang),
        }
    }

    if (email !== '') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
            return {
                field: 'email',
                message: t.signature.validationEmail(lang),
            }
        }
    }
    return null
}

export const signatureItemToFormData = (item: SignatureItem): SignatureFormData => {
    const tags: LogoTag[] = []
    if (item.play_logo) tags.push('play')
    if (item.nope_logo) tags.push('nope')
    if (item.alliance_logo) tags.push('alliance')

    const rows: InfoRow[] =
        item.rows?.map((row) => ({
            id: row.id,
            title: row.label || '',
            content: row.value || '',
            link: row.url || '',
            isLink: !!row.url,
        })) || []

    return {
        name: item.first_and_last_name || '',
        position: item.position || '',
        email: item.email || '',
        tags,
        rows,
        imageUrl: item.image || undefined,
        company: item.company || undefined,
    }
}

const mapFormDataToBase = (formData: SignatureFormData) => {
    const playLogo = formData.tags.includes('play')
    const nopeLogo = formData.tags.includes('nope')
    const allianceLogo = formData.tags.includes('alliance')
    const company: 'nope' | 'play' | undefined = formData.company || undefined

    return {
        first_and_last_name: formData.name || null,
        position: formData.position || null,
        email: formData.email?.trim() || null,
        play_logo: playLogo,
        nope_logo: nopeLogo,
        alliance_logo: allianceLogo,
        company: company || 'nope',
        image: formData.imageUrl || null,
    }
}

const mapRows = (rows: InfoRow[], signatureId?: number) =>
    rows
        .filter((row) => row.title.trim() || row.content.trim())
        .map((row, index) => ({
            ...(signatureId !== undefined ? { id: row.id, signature_id: signatureId } : {}),
            label: row.title || null,
            value: row.content || null,
            url: row.isLink && row.link ? row.link : null,
            order: index,
        }))

export const formDataToSignatureUpdate = (
    formData: SignatureFormData,
    signatureId?: number,
): SignatureUpdate => ({
    id: signatureId,
    ...mapFormDataToBase(formData),
    rows: mapRows(formData.rows, signatureId),
})

export const formDataToSignatureInsertWithRows = (
    formData: SignatureFormData,
): SignatureInsertWithRows => ({
    ...mapFormDataToBase(formData),
    rows: mapRows(formData.rows),
})
